import clsx, { type ClassValue } from "clsx";
import { twMerge } from "utils/tailwind-merge.config";

/**
 * Leaf presentation helpers. Keep this module independent of game registries so
 * registry-owned renderers can safely use it during module initialization.
 */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export function getPokemonSetIconUrl(setCode: string): string | null {
  if (!setCode) return null;
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return null;
  return `${cdnBase}/set-icon/pokemon/${setCode}`;
}

export const costPrintedToManaFontClasses = (costPrinted: string): string[] => {
  const matches = costPrinted.match(/{(.{1,3})}/g);
  if (!matches) return [];

  return matches.map((match) =>
    match.replaceAll(/[{}]/g, "").replace("/", "").toLocaleLowerCase()
  );
};

export const firstUpper = (value: string) =>
  value ? `${value[0].toUpperCase()}${value.slice(1)}` : value;

export const capitalizeFirstLetter = (value: string) =>
  value?.charAt(0).toUpperCase() + value.slice(1);

export const planesWalkerPairs = {
  "[+1]": "1",
  "[+2]": "2",
  "[+3]": "3",
  "[+4]": "4",
  "[+5]": "5",
  "[+6]": "6",
  "[+7]": "7",
  "[+8]": "8",
  "[+9]": "9",
  "[+10]": "10",
  "[+11]": "11",
  "[+12]": "12",
  "[+13]": "13",
  "[+14]": "14",
  "[+15]": "15",
  "[+16]": "16",
  "[+17]": "17",
  "[+18]": "18",
  "[+19]": "19",
  "[+20]": "20",
  "[0]": "0",
  "[−1]": "1",
  "[−2]": "2",
  "[−3]": "3",
  "[−4]": "4",
  "[−5]": "5",
  "[−6]": "6",
  "[−7]": "7",
  "[−8]": "8",
  "[−9]": "9",
  "[−10]": "10",
  "[−11]": "11",
  "[−12]": "12",
  "[−13]": "13",
  "[−14]": "14",
  "[−15]": "15",
  "[−16]": "16",
  "[−17]": "17",
  "[−18]": "18",
  "[−19]": "19",
  "[−20]": "20",
};

export const manaSymbolsPairs = {
  "{T}": "tap",
  "{Q}": "untap",
  "{E}": "e",
  "{0}": "0",
  "{1}": "1",
  "{2}": "2",
  "{3}": "3",
  "{4}": "4",
  "{5}": "5",
  "{6}": "6",
  "{7}": "7",
  "{8}": "8",
  "{9}": "9",
  "{10}": "10",
  "{11}": "11",
  "{12}": "12",
  "{13}": "13",
  "{14}": "14",
  "{15}": "15",
  "{16}": "16",
  "{17}": "17",
  "{18}": "18",
  "{19}": "19",
  "{20}": "20",
  "{W/U}": "wu",
  "{W/B}": "wb",
  "{B/R}": "br",
  "{B/G}": "bg",
  "{U/B}": "ub",
  "{U/R}": "ur",
  "{R/G}": "rg",
  "{R/W}": "rw",
  "{G/W}": "gw",
  "{G/U}": "gu",
  "{2/W}": "2w",
  "{2/U}": "2u",
  "{2/B}": "2b",
  "{2/R}": "2r",
  "{2/G}": "2g",
  "{W/P}": "wp",
  "{U/P}": "up",
  "{B/P}": "bp",
  "{R/P}": "rp",
  "{G/P}": "gp",
  "{W}": "w",
  "{U}": "u",
  "{B}": "b",
  "{R}": "r",
  "{G}": "g",
  "{C}": "c",
  "{S}": "s",
  "{X}": "x",
};

export const containsHtmlTags = (text: string): boolean => /<[^>]+>/.test(text);

export const VOID_HTML_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

export const isVoidElement = (elementName: string): boolean =>
  VOID_HTML_ELEMENTS.has(elementName.toLowerCase());
